import { useEffect, useState } from "react";
import api, { extractError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Trash, UploadSimple, PencilSimple, Plus, Ticket, Check, X, Gift, Users, DownloadSimple, Receipt } from "@phosphor-icons/react";
import { periodText } from "@/components/SubscribeButton";
import { SubscriptionsTable } from "@/components/SubscriptionsTable";
import { OfferReport } from "@/components/OfferReport";

// ---------- Tools ----------
function ToolUploadDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", slug: "", description: "", category: "General", price_inr: 299, video_url: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Select an HTML file");
    setBusy(true);
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    fd.append("file", file);
    try {
      await api.post("/admin/tools/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Tool uploaded");
      setOpen(false); setForm({ name: "", slug: "", description: "", category: "General", price_inr: 299, video_url: "" }); setFile(null);
      onCreated();
    } catch (err) { toast.error(extractError(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="upload-tool-btn" className="bg-primary text-black hover:bg-primary/90 font-bold rounded-sm"><UploadSimple size={14} weight="bold" className="mr-2" /> Upload HTML tool</Button></DialogTrigger>
      <DialogContent className="bg-card border-border rounded-sm max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display font-extrabold text-2xl">Upload HTML Tool</DialogTitle><DialogDescription>Monthly subscription. Set price in ₹/month.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2"><Label className="text-xs font-mono uppercase">Name</Label><Input required data-testid="tool-upload-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="space-y-2"><Label className="text-xs font-mono uppercase">Slug</Label><Input required data-testid="tool-upload-slug" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="my-tool" /></div>
          </div>
          <div className="space-y-2"><Label className="text-xs font-mono uppercase">Description</Label><Textarea rows={2} data-testid="tool-upload-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2"><Label className="text-xs font-mono uppercase">Category</Label><Input data-testid="tool-upload-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} /></div>
            <div className="space-y-2"><Label className="text-xs font-mono uppercase">Price (₹/mo)</Label><Input type="number" min={0} data-testid="tool-upload-price" value={form.price_inr} onChange={(e) => setForm({ ...form, price_inr: Number(e.target.value) })} /></div>
          </div>
          <div className="space-y-2"><Label className="text-xs font-mono uppercase">YouTube tutorial URL <span className="text-muted-foreground normal-case tracking-normal">(optional)</span></Label><Input type="url" data-testid="tool-upload-video" value={form.video_url} onChange={(e) => setForm({ ...form, video_url: e.target.value })} placeholder="https://youtu.be/xxxxxxxx" /></div>
          <div className="space-y-2"><Label className="text-xs font-mono uppercase">HTML File</Label><Input type="file" accept=".html,.htm" required data-testid="tool-upload-file" onChange={(e) => setFile(e.target.files?.[0] || null)} /></div>
          <DialogFooter><Button type="submit" disabled={busy} data-testid="tool-upload-submit" className="bg-primary text-black hover:bg-primary/90 font-bold rounded-sm">{busy ? "Uploading..." : "Upload"}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ToolEditDialog({ tool, onUpdated }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(tool.name);
  const [desc, setDesc] = useState(tool.description);
  const [price, setPrice] = useState(tool.price_inr);
  const [videoUrl, setVideoUrl] = useState(tool.video_url || "");
  const [planId, setPlanId] = useState(tool.razorpay_plan_id || "");
  const [htmlFile, setHtmlFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const payload = { name, description: desc, price_inr: Number(price), video_url: videoUrl };
      if (planId.trim() !== (tool.razorpay_plan_id || "")) payload.razorpay_plan_id = planId.trim();
      if (htmlFile) {
        const html = await htmlFile.text();
        if (!html || !html.trim()) throw new Error("HTML file is empty");
        payload.html = html;
      }
      await api.patch(`/admin/tools/${tool.id}`, payload);
      toast.success(htmlFile ? "Tool updated (HTML replaced)" : "Tool updated");
      setHtmlFile(null); setOpen(false); onUpdated();
    } catch (err) { toast.error(extractError(err) || err.message); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="outline" className="rounded-sm h-8" data-testid={`edit-tool-${tool.slug}`}><PencilSimple size={14} weight="bold" /></Button></DialogTrigger>
      <DialogContent className="bg-card border-border rounded-sm max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Edit Tool</DialogTitle><DialogDescription>{tool.slug}</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2"><Label className="text-xs font-mono uppercase">Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} /></div>
          <div className="space-y-2"><Label className="text-xs font-mono uppercase">Description</Label><Textarea rows={2} value={desc} onChange={(e) => setDesc(e.target.value)} /></div>
          <div className="space-y-2"><Label className="text-xs font-mono uppercase">Price (₹/mo)</Label><Input type="number" min={0} value={price} onChange={(e) => setPrice(e.target.value)} data-testid={`edit-tool-price-${tool.slug}`} /></div>
          <div className="space-y-2"><Label className="text-xs font-mono uppercase">YouTube tutorial URL <span className="text-muted-foreground normal-case tracking-normal">(optional)</span></Label><Input type="url" value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)} data-testid={`edit-tool-video-${tool.slug}`} placeholder="https://youtu.be/xxxxxxxx" /></div>
          <div className="space-y-2">
            <Label className="text-xs font-mono uppercase">Razorpay Plan ID <span className="text-muted-foreground normal-case tracking-normal">(makes this tool subscription-only)</span></Label>
            <Input value={planId} onChange={(e) => setPlanId(e.target.value)} data-testid={`edit-tool-plan-${tool.slug}`} placeholder="plan_XXXXXXXXXXXX" className="font-mono" />
            <p className="text-xs text-muted-foreground">With a plan ID, the price is taken from the Razorpay plan and one-time cart purchase is removed. Clear it to switch back to one-time purchase.</p>
          </div>
          <div className="space-y-2 border-t border-border pt-3">
            <Label className="text-xs font-mono uppercase">Replace HTML file <span className="text-muted-foreground normal-case tracking-normal">(optional — leave empty to keep current)</span></Label>
            <Input type="file" accept=".html,.htm" onChange={(e) => setHtmlFile(e.target.files?.[0] || null)} data-testid={`edit-tool-html-${tool.slug}`} />
            {htmlFile && <p className="text-xs font-mono text-primary">New file: {htmlFile.name} ({Math.round(htmlFile.size / 1024)} KB) — click Save to replace</p>}
            <p className="text-xs text-muted-foreground">Existing student access, expiry, and subscriptions are preserved. Only the tool's HTML content will change.</p>
          </div>
        </div>
        <DialogFooter><Button onClick={save} disabled={busy} data-testid={`edit-tool-save-${tool.slug}`} className="bg-primary text-black hover:bg-primary/90 font-bold rounded-sm">{busy ? "Saving..." : "Save"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------- User Groups ----------
function UserGroupsDialog({ user, onUpdated }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState((user.groups || []).join(", "));
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const groups = text.split(",").map((g) => g.trim()).filter(Boolean);
      await api.post(`/admin/users/${user.id}/groups`, { groups });
      toast.success("Groups updated");
      setOpen(false); onUpdated();
    } catch (err) { toast.error(extractError(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="text-xs font-mono text-primary hover:underline" data-testid={`edit-groups-${user.email}`}>
          {(user.groups || []).length > 0 ? user.groups.join(", ") : "+ add"}
        </button>
      </DialogTrigger>
      <DialogContent className="bg-card border-border rounded-sm">
        <DialogHeader>
          <DialogTitle>Edit groups for {user.email}</DialogTitle>
          <DialogDescription>Comma-separated tags (e.g. <code>nifty-batch-2026, pro-traders</code>). Discount codes can target specific groups.</DialogDescription>
        </DialogHeader>
        <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="group1, group2" data-testid={`groups-input-${user.email}`} />
        <DialogFooter><Button onClick={save} disabled={busy} data-testid={`save-groups-${user.email}`} className="bg-primary text-black hover:bg-primary/90 font-bold rounded-sm">{busy ? "Saving..." : "Save"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Coupons ----------
function CouponDialog({ initial, onDone, tools, users, groups }) {
  const isEdit = !!initial;
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    code: initial?.code || "",
    percent_off: initial?.percent_off || (initial?.free_days ? 0 : 10),
    free_days: initial?.free_days || 0,
    tool_ids: initial?.tool_ids || [],
    is_active: initial?.is_active ?? true,
    valid_until: initial?.valid_until || "",
    max_uses: initial?.max_uses || "",
    allowed_user_ids: initial?.allowed_user_ids || [],
    allowed_groups: initial?.allowed_groups || [],
  });
  const [busy, setBusy] = useState(false);

  const toggleList = (key, val) => setForm((f) => {
    const arr = f[key];
    return { ...f, [key]: arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val] };
  });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = {
      percent_off: Number(form.free_days) > 0 ? 0 : Number(form.percent_off),
      free_days: Number(form.free_days) || 0,
      tool_ids: Number(form.free_days) > 0 ? form.tool_ids : [],
      allowed_user_ids: form.allowed_user_ids,
      allowed_groups: form.allowed_groups,
      valid_until: form.valid_until || null,
      max_uses: form.max_uses ? Number(form.max_uses) : null,
      is_active: form.is_active,
    };
    try {
      if (isEdit) {
        await api.patch(`/admin/coupons/${initial.id}`, payload);
        toast.success("Coupon updated");
      } else {
        await api.post("/admin/coupons", { code: form.code.toUpperCase().trim(), ...payload });
        toast.success("Coupon created");
      }
      setOpen(false); onDone();
    } catch (err) { toast.error(extractError(err)); } finally { setBusy(false); }
  };

  const scope = form.allowed_user_ids.length === 0 && form.allowed_groups.length === 0
    ? "General (anyone can use)"
    : `${form.allowed_user_ids.length} user(s) + ${form.allowed_groups.length} group(s)`;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {isEdit
          ? <Button size="sm" variant="outline" className="rounded-sm h-8" data-testid={`edit-coupon-${initial.code}`}><PencilSimple size={14} weight="bold" /></Button>
          : <Button data-testid="create-coupon-btn" className="bg-primary text-black hover:bg-primary/90 font-bold rounded-sm"><Plus size={14} weight="bold" className="mr-2" /> New coupon</Button>}
      </DialogTrigger>
      <DialogContent className="bg-card border-border rounded-sm max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display font-extrabold text-2xl">{isEdit ? `Edit: ${initial.code}` : "Create Discount Code"}</DialogTitle>
          <DialogDescription>Applied on top of the bundle discount at checkout. Leave user/group lists empty to make the code public.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-xs font-mono uppercase">Code</Label>
              <Input required disabled={isEdit} value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} data-testid="coupon-code" placeholder="LAUNCH25" className="font-mono uppercase" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-mono uppercase">Percent off</Label>
              <Input type="number" min={0} max={100} required disabled={Number(form.free_days) > 0} data-testid="coupon-pct" value={form.percent_off} onChange={(e) => setForm({ ...form, percent_off: e.target.value })} />
            </div>
          </div>
          <div className="space-y-2 border border-primary/30 bg-primary/5 p-3 rounded-sm">
            <Label className="text-xs font-mono uppercase">Free period on subscriptions, in days (optional)</Label>
            <div className="flex gap-2">
              <Input type="number" min={0} max={730} value={form.free_days} onChange={(e) => setForm({ ...form, free_days: e.target.value })} data-testid="coupon-free-days" placeholder="0" />
              {[30, 60, 90].map((d) => (
                <Button key={d} type="button" variant="outline" size="sm" className="rounded-sm h-10" onClick={() => setForm({ ...form, free_days: d })} data-testid={`coupon-free-preset-${d}`}>{d / 30} mo</Button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">Above 0 turns this into a subscription free-trial code: the first auto-debit is delayed by this many days and access starts right away. Usable once per tool per user. Percent off is ignored.</p>
            {Number(form.free_days) > 0 && (
              <div className="pt-2">
                <div className="text-xs font-mono uppercase text-muted-foreground mb-1">Works on · {form.tool_ids.length === 0 ? "all subscription tools" : `${form.tool_ids.length} selected tool(s)`}</div>
                <div className="space-y-1 border border-border rounded-sm p-2 max-h-40 overflow-y-auto">
                  {tools.filter((t) => t.is_subscription).map((t) => (
                    <label key={t.id} className="flex items-center gap-2 text-sm cursor-pointer p-1 hover:bg-secondary rounded-sm">
                      <input type="checkbox" checked={form.tool_ids.includes(t.id)} onChange={() => toggleList("tool_ids", t.id)} data-testid={`coupon-tool-${t.slug}`} className="accent-primary" />
                      <span className="text-xs">{t.name}</span>
                    </label>
                  ))}
                  {tools.filter((t) => t.is_subscription).length === 0 && <div className="text-xs text-muted-foreground p-1">No subscription tools yet. Add a Razorpay Plan ID to a tool first.</div>}
                </div>
                <p className="text-[11px] text-muted-foreground mt-1">Leave all unticked to allow every subscription tool.</p>
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-xs font-mono uppercase">Valid until (optional)</Label>
              <Input type="datetime-local" value={form.valid_until ? new Date(new Date(form.valid_until).getTime() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16) : ""} onChange={(e) => setForm({ ...form, valid_until: e.target.value ? new Date(e.target.value).toISOString() : "" })} data-testid="coupon-valid-until" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-mono uppercase">Max uses (optional)</Label>
              <Input type="number" min={1} value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: e.target.value })} data-testid="coupon-max-uses" placeholder="unlimited" />
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} data-testid="coupon-active" className="accent-primary" />
            <span className="font-mono uppercase text-xs tracking-widest">Active</span>
          </label>

          <div className="space-y-2 border border-border p-3 rounded-sm bg-black/20">
            <div className="text-xs font-mono uppercase text-muted-foreground">Scope · {scope}</div>

            <details className="text-sm">
              <summary className="cursor-pointer text-xs font-mono uppercase tracking-widest text-primary py-1">Restrict to specific students ({form.allowed_user_ids.length})</summary>
              <div className="max-h-40 overflow-y-auto space-y-1 mt-2 border border-border rounded-sm p-2">
                {users.filter((u) => u.role !== "admin").map((u) => (
                  <label key={u.id} className="flex items-center gap-2 text-sm cursor-pointer p-1 hover:bg-secondary rounded-sm">
                    <input type="checkbox" checked={form.allowed_user_ids.includes(u.id)} onChange={() => toggleList("allowed_user_ids", u.id)} data-testid={`coupon-user-${u.email}`} className="accent-primary" />
                    <span className="font-mono text-xs">{u.email}</span>
                  </label>
                ))}
                {users.filter((u) => u.role !== "admin").length === 0 && <div className="text-xs text-muted-foreground p-1">No students yet</div>}
              </div>
            </details>

            <details className="text-sm">
              <summary className="cursor-pointer text-xs font-mono uppercase tracking-widest text-primary py-1">Restrict to specific groups ({form.allowed_groups.length})</summary>
              <div className="mt-2 border border-border rounded-sm p-2 space-y-1">
                {groups.length === 0 && <div className="text-xs text-muted-foreground p-1">No groups yet. Tag users first (Users tab).</div>}
                {groups.map((g) => (
                  <label key={g} className="flex items-center gap-2 text-sm cursor-pointer p-1 hover:bg-secondary rounded-sm">
                    <input type="checkbox" checked={form.allowed_groups.includes(g)} onChange={() => toggleList("allowed_groups", g)} data-testid={`coupon-group-${g}`} className="accent-primary" />
                    <span className="font-mono text-xs">{g}</span>
                  </label>
                ))}
              </div>
            </details>
          </div>

          <DialogFooter><Button type="submit" disabled={busy} data-testid="coupon-submit" className="bg-primary text-black hover:bg-primary/90 font-bold rounded-sm">{busy ? "Saving..." : (isEdit ? "Save" : "Create coupon")}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Access Codes ----------
function AccessCodeCreateDialog({ tools, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ code: "", description: "", duration_days: 90, is_active: true });
  const [selectedTools, setSelectedTools] = useState([]);
  const [busy, setBusy] = useState(false);

  const toggle = (id) => setSelectedTools((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  const submit = async (e) => {
    e.preventDefault();
    if (selectedTools.length === 0) return toast.error("Pick at least one tool");
    setBusy(true);
    try {
      await api.post("/admin/access-codes", { ...form, tool_ids: selectedTools, duration_days: Number(form.duration_days) });
      toast.success("Access code created — now add student emails");
      setOpen(false);
      setForm({ code: "", description: "", duration_days: 90, is_active: true });
      setSelectedTools([]);
      onCreated();
    } catch (err) { toast.error(extractError(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="create-access-code-btn" className="bg-primary text-black hover:bg-primary/90 font-bold rounded-sm"><Plus size={14} weight="bold" className="mr-2" /> New access code</Button></DialogTrigger>
      <DialogContent className="bg-card border-border rounded-sm max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display font-extrabold text-2xl">Create Free-Access Code</DialogTitle>
          <DialogDescription>Grants selected tools free for N days from signup. Restricted to students on the email allowlist.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label className="text-xs font-mono uppercase">Code</Label>
            <Input required value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} data-testid="ac-code" placeholder="NIFTY2026" className="font-mono uppercase" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-mono uppercase">Description (optional)</Label>
            <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="ac-desc" placeholder="Batch details, etc." />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-xs font-mono uppercase">Duration (days)</Label>
              <Input type="number" min={1} max={3650} required value={form.duration_days} onChange={(e) => setForm({ ...form, duration_days: e.target.value })} data-testid="ac-duration" />
              <p className="text-xs text-muted-foreground">90 = ~3 months</p>
            </div>
            <label className="flex items-end gap-2 text-sm cursor-pointer pb-2">
              <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} data-testid="ac-active" className="accent-primary" />
              <span className="font-mono uppercase text-xs tracking-widest">Active</span>
            </label>
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-mono uppercase">Tools granted</Label>
            <div className="border border-border rounded-sm p-3 space-y-1 max-h-48 overflow-y-auto">
              {tools.map((t) => (
                <label key={t.id} className="flex items-center gap-2 text-sm cursor-pointer p-1 hover:bg-secondary rounded-sm">
                  <input type="checkbox" checked={selectedTools.includes(t.id)} onChange={() => toggle(t.id)} data-testid={`ac-tool-${t.slug}`} className="accent-primary" />
                  <span>{t.name}</span>
                  <span className="text-xs font-mono text-muted-foreground ml-auto">₹{t.price_inr}/mo</span>
                </label>
              ))}
              {tools.length === 0 && <div className="text-xs text-muted-foreground p-1">No tools yet — create some first</div>}
            </div>
          </div>
          <DialogFooter><Button type="submit" disabled={busy} data-testid="ac-submit" className="bg-primary text-black hover:bg-primary/90 font-bold rounded-sm">{busy ? "Creating..." : "Create code"}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AccessCodeManageDialog({ code, tools, onUpdated }) {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [bulk, setBulk] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/access-codes/${code.id}`);
      setDetail(data);
    } catch (err) { toast.error(extractError(err)); }
  };

  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open]);

  const parseTextEntries = () => {
    return bulk.split("\n").map((line) => {
      const parts = line.split(/[,\t]/).map((p) => p.trim());
      const email = (parts[0] || "").toLowerCase();
      const name = parts[1] || "";
      return { email, name };
    }).filter((e) => e.email && e.email.includes("@") && e.email.includes("."));
  };

  const addFromText = async () => {
    const entries = parseTextEntries();
    if (entries.length === 0) return toast.error("Paste at least one valid email");
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/access-codes/${code.id}/allowlist/bulk`, { entries });
      toast.success(`Added ${data.added} · Skipped ${data.skipped}`);
      setBulk("");
      load(); onUpdated();
    } catch (err) { toast.error(extractError(err)); } finally { setBusy(false); }
  };

  const uploadCsv = async () => {
    if (!file) return toast.error("Select a CSV file");
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/admin/access-codes/${code.id}/allowlist/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Parsed ${data.parsed} · Added ${data.added} · Skipped ${data.skipped}`);
      setFile(null);
      load(); onUpdated();
    } catch (err) { toast.error(extractError(err)); } finally { setBusy(false); }
  };

  const remove = async (email) => {
    if (!window.confirm(`Remove ${email}?`)) return;
    try { await api.delete(`/admin/access-codes/${code.id}/allowlist/${encodeURIComponent(email)}`); toast.success("Removed"); load(); onUpdated(); }
    catch (err) { toast.error(extractError(err)); }
  };

  const toolNames = (tool_ids) => (tool_ids || []).map((tid) => tools.find((t) => t.id === tid)?.name || tid).join(", ");
  const orphanIds = (code.tool_ids || []).filter((tid) => !tools.find((t) => t.id === tid));
  const hasOrphans = orphanIds.length > 0;

  const [reattachTools, setReattachTools] = useState([]);
  const toggleReattach = (id) => setReattachTools((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  // Initialize reattachTools with currently-attached (valid) tools when dialog opens
  useEffect(() => {
    if (open) {
      const currentValid = (code.tool_ids || []).filter((tid) => tools.find((t) => t.id === tid));
      setReattachTools(currentValid);
    }
    // eslint-disable-next-line
  }, [open, code.tool_ids]);

  const runBackfill = async () => {
    if (reattachTools.length === 0) return toast.error("Select at least one tool");
    const usedCount = detail?.allowlist_used || 0;
    const msg = usedCount > 0
      ? `Update ${code.code} to ${reattachTools.length} tool(s) AND re-sync ${usedCount} already-redeemed student(s)?`
      : `Update ${code.code} to ${reattachTools.length} tool(s)?`;
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/access-codes/${code.id}/backfill`, { tool_ids: reattachTools });
      toast.success(`Done! Updated ${data.users_updated} student(s) with ${data.subs_added} tool subscription(s).`);
      load(); onUpdated();
    } catch (err) { toast.error(extractError(err)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="outline" className="rounded-sm h-8" data-testid={`manage-ac-${code.code}`}><Users size={14} weight="bold" className="mr-1" /> Manage</Button></DialogTrigger>
      <DialogContent className="bg-card border-border rounded-sm max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display font-extrabold text-2xl flex items-center gap-2"><Gift size={20} weight="duotone" className="text-primary" /> {code.code}</DialogTitle>
          <DialogDescription>{code.description || "—"} · {code.duration_days} days · Tools: {toolNames(code.tool_ids) || "none"}</DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          <section className={`border p-4 rounded-sm ${hasOrphans ? "border-destructive/60 bg-destructive/10" : "border-border"}`} data-testid={`ac-tools-section-${code.code}`}>
            <h4 className={`font-display font-bold text-sm uppercase tracking-widest mb-2 ${hasOrphans ? "text-destructive" : "text-muted-foreground"}`}>
              {hasOrphans ? "⚠ Broken tool references — fix now" : "Attached tools"}
            </h4>
            <p className="text-sm text-muted-foreground mb-3">
              {hasOrphans
                ? <>This code points to <strong>{orphanIds.length}</strong> deleted tool(s). Students see nothing in library. Pick the current tools to re-attach and auto-fix all <strong>{detail?.allowlist_used || 0}</strong> already-redeemed students.</>
                : <>Select which tools this code grants. Changes will also update all <strong>{detail?.allowlist_used || 0}</strong> already-redeemed students. Existing expiry is preserved.</>
              }
            </p>
            <div className="border border-border rounded-sm p-2 max-h-48 overflow-y-auto space-y-1 bg-card">
              {tools.map((t) => (
                <label key={t.id} className="flex items-center gap-2 text-sm cursor-pointer p-1 hover:bg-secondary rounded-sm">
                  <input type="checkbox" checked={reattachTools.includes(t.id)} onChange={() => toggleReattach(t.id)} data-testid={`ac-reattach-${code.code}-${t.slug}`} />
                  <span>{t.name}</span>
                  <span className="ml-auto text-xs font-mono text-muted-foreground">₹{t.price_inr}</span>
                </label>
              ))}
              {tools.length === 0 && <div className="text-xs text-muted-foreground p-2">No tools available. Upload tools first.</div>}
            </div>
            <Button onClick={runBackfill} disabled={busy || reattachTools.length === 0} data-testid={`ac-backfill-${code.code}`} className="mt-3 rounded-sm bg-primary text-black hover:bg-primary/90 font-bold">
              {busy ? "Saving..." : (detail?.allowlist_used > 0 ? `Save & re-sync ${detail.allowlist_used} student(s)` : "Save attached tools")}
            </Button>
          </section>

          <section className="border border-border p-4 rounded-sm">
            <h4 className="font-display font-bold text-sm uppercase tracking-widest text-muted-foreground mb-3">Add students to allowlist</h4>
            <div className="space-y-4">
              <div>
                <Label className="text-xs font-mono uppercase mb-2 block">Paste emails (one per line, format: <code>email, name</code>)</Label>
                <Textarea rows={4} value={bulk} onChange={(e) => setBulk(e.target.value)} data-testid={`ac-bulk-text-${code.code}`} placeholder={"rakesh@example.com, Rakesh\npriya@example.com, Priya"} className="font-mono text-sm" />
                <Button size="sm" onClick={addFromText} disabled={busy} data-testid={`ac-bulk-submit-${code.code}`} className="mt-2 rounded-sm">Add pasted entries</Button>
              </div>
              <div className="border-t border-border pt-3">
                <Label className="text-xs font-mono uppercase mb-2 block">Or upload CSV (columns: <code>email,name</code>)</Label>
                <div className="flex gap-2">
                  <Input type="file" accept=".csv" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid={`ac-csv-file-${code.code}`} />
                  <Button size="sm" onClick={uploadCsv} disabled={busy} data-testid={`ac-csv-upload-${code.code}`} className="rounded-sm gap-2"><UploadSimple size={14} weight="bold" /> Upload</Button>
                </div>
              </div>
            </div>
          </section>

          <section className="border border-border p-4 rounded-sm">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-display font-bold text-sm uppercase tracking-widest text-muted-foreground">Allowlist ({detail?.allowlist_total || 0})</h4>
              <div className="text-xs font-mono text-muted-foreground">Used {detail?.allowlist_used || 0} · Remaining {detail?.allowlist_remaining || 0}</div>
            </div>
            <div className="border border-border rounded-sm max-h-64 overflow-y-auto">
              <Table>
                <TableHeader><TableRow><TableHead>Email</TableHead><TableHead>Name</TableHead><TableHead>Status</TableHead><TableHead className="text-right"></TableHead></TableRow></TableHeader>
                <TableBody>
                  {(detail?.allowlist || []).map((e) => (
                    <TableRow key={e.email} data-testid={`ac-entry-${e.email}`}>
                      <TableCell className="font-mono text-xs">{e.email}</TableCell>
                      <TableCell className="text-sm">{e.name || "—"}</TableCell>
                      <TableCell>
                        {e.used_by_user_id
                          ? <span className="text-xs font-mono text-primary">redeemed {e.used_at ? new Date(e.used_at).toLocaleDateString() : ""}</span>
                          : <span className="text-xs font-mono text-muted-foreground">pending</span>}
                      </TableCell>
                      <TableCell className="text-right">
                        {!e.used_by_user_id && (
                          <button onClick={() => remove(e.email)} className="p-1 hover:text-destructive" data-testid={`ac-remove-${e.email}`}><Trash size={14} weight="bold" /></button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {(detail?.allowlist || []).length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-6 text-sm">No allowlist entries yet.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Bulk Grant Dialog ----------
function BulkGrantDialog({ tools, onGranted }) {
  const [open, setOpen] = useState(false);
  const [emailsText, setEmailsText] = useState("");
  const [selectedTools, setSelectedTools] = useState([]);
  const [duration, setDuration] = useState(90);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const toggleTool = (id) => setSelectedTools((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  const submit = async (e) => {
    e.preventDefault();
    const emails = emailsText.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean);
    if (emails.length === 0) return toast.error("Add at least one email");
    if (selectedTools.length === 0) return toast.error("Pick at least one tool");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/grants/bulk", { emails, tool_ids: selectedTools, duration_days: Number(duration) });
      setResult(data);
      toast.success(`Granted access to ${data.granted_count} user(s)`);
      onGranted?.();
    } catch (err) { toast.error(extractError(err)); }
    finally { setBusy(false); }
  };

  const reset = () => { setEmailsText(""); setSelectedTools([]); setDuration(90); setResult(null); };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <Button className="rounded-sm bg-primary text-black hover:bg-primary/90 font-bold" data-testid="bulk-grant-btn"><Gift size={16} weight="bold" className="mr-2" /> Bulk grant</Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-display">Bulk grant free access</DialogTitle>
          <DialogDescription>Paste emails of existing users. They'll get free access to the selected tools for the chosen duration.</DialogDescription>
        </DialogHeader>
        {result ? (
          <div className="space-y-4" data-testid="bulk-grant-result">
            <div className="p-4 border border-primary/40 bg-primary/5 rounded-sm">
              <div className="font-bold text-primary mb-2">Granted access to {result.granted_count} user(s)</div>
              <div className="text-xs font-mono text-muted-foreground">{result.tools_per_user} tool(s) · {result.duration_days} days</div>
              {result.granted_emails?.length > 0 && (
                <div className="mt-3 text-xs">
                  <div className="font-mono uppercase text-muted-foreground mb-1">Granted</div>
                  <div className="max-h-32 overflow-y-auto font-mono text-xs">{result.granted_emails.join(", ")}</div>
                </div>
              )}
              {result.missing_emails?.length > 0 && (
                <div className="mt-3 text-xs">
                  <div className="font-mono uppercase text-destructive mb-1">Not signed up yet ({result.missing_emails.length})</div>
                  <div className="max-h-32 overflow-y-auto font-mono text-xs text-muted-foreground">{result.missing_emails.join(", ")}</div>
                  <p className="text-xs text-muted-foreground mt-2">Tip: For not-yet-signed-up students, create an Access Code with these emails on the allowlist instead.</p>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" className="rounded-sm" onClick={reset} data-testid="bulk-grant-again">Grant another batch</Button>
              <Button className="rounded-sm bg-primary text-black hover:bg-primary/90" onClick={() => setOpen(false)} data-testid="bulk-grant-done">Done</Button>
            </DialogFooter>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1">
              <Label className="text-xs font-mono uppercase">Emails (comma, space, or newline separated)</Label>
              <Textarea rows={5} placeholder="student1@example.com, student2@example.com" value={emailsText} onChange={(e) => setEmailsText(e.target.value)} data-testid="bulk-grant-emails" className="rounded-sm font-mono text-sm" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-mono uppercase">Tools</Label>
              <div className="border border-border rounded-sm p-2 max-h-40 overflow-y-auto space-y-1">
                {tools.map((t) => (
                  <label key={t.id} className="flex items-center gap-2 text-sm cursor-pointer p-1 hover:bg-secondary rounded-sm">
                    <input type="checkbox" checked={selectedTools.includes(t.id)} onChange={() => toggleTool(t.id)} data-testid={`bulk-grant-tool-${t.slug}`} />
                    <span>{t.name}</span>
                    <span className="ml-auto text-xs font-mono text-muted-foreground">₹{t.price_inr}</span>
                  </label>
                ))}
                {tools.length === 0 && <div className="text-xs text-muted-foreground p-2">No tools available</div>}
              </div>
            </div>
            <div className="space-y-1 max-w-[200px]">
              <Label className="text-xs font-mono uppercase">Duration (days)</Label>
              <Input type="number" min={1} max={3650} value={duration} onChange={(e) => setDuration(e.target.value)} data-testid="bulk-grant-duration" className="rounded-sm" />
            </div>
            <DialogFooter>
              <Button type="submit" disabled={busy} className="rounded-sm bg-primary text-black hover:bg-primary/90 font-bold" data-testid="bulk-grant-submit">{busy ? "Granting..." : "Grant access"}</Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------- Per-user Grant Popover ----------
function UserGrantPopover({ userId, userEmail, tools, onGranted }) {
  const [duration, setDuration] = useState(30);
  return (
    <details className="relative">
      <summary className="text-xs font-mono cursor-pointer hover:text-primary list-none" data-testid={`grant-open-${userEmail}`}>Grant tool ▾</summary>
      <div className="absolute right-0 top-6 z-10 bg-card border border-border rounded-sm p-3 min-w-[260px] space-y-2 shadow-lg">
        <div className="space-y-1">
          <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Duration (days)</Label>
          <Input type="number" min={1} max={3650} value={duration} onChange={(e) => setDuration(e.target.value)} data-testid={`grant-duration-${userEmail}`} className="rounded-sm h-8 text-sm" />
        </div>
        <div className="border-t border-border pt-2 space-y-1 max-h-48 overflow-y-auto">
          {tools.map((t) => (
            <button key={t.id} type="button" onClick={() => onGranted(userId, t.id, duration)} data-testid={`grant-tool-${userEmail}-${t.slug}`} className="w-full text-left text-xs px-2 py-1.5 hover:bg-secondary rounded-sm flex items-center justify-between">
              <span>{t.name}</span>
              <span className="text-primary text-[10px] font-mono">+{duration}d</span>
            </button>
          ))}
          {tools.length === 0 && <div className="text-xs text-muted-foreground p-1">No tools</div>}
        </div>
      </div>
    </details>
  );
}

// ---------- Orders / Buyers ----------
function OrdersTable({ tools }) {
  const [orders, setOrders] = useState([]);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [filterToolId, setFilterToolId] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = filterToolId ? { tool_id: filterToolId } : {};
      const { data } = await api.get("/admin/orders", { params });
      setOrders(data.orders || []);
      setTotalRevenue(data.total_revenue_inr || 0);
    } catch (err) { toast.error(extractError(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterToolId]);

  const exportCsv = () => {
    if (orders.length === 0) return toast.error("No orders to export");
    const headers = ["Paid at", "Email", "Name", "Tools", "Amount (₹)", "Coupon", "Payment ID"];
    const rows = orders.map((o) => [
      o.paid_at ? new Date(o.paid_at).toISOString() : "",
      o.user_email, o.user_name,
      (o.tools || []).join(" | "),
      o.amount_inr, o.coupon_code || "",
      o.payment_id || "",
    ]);
    const csv = [headers, ...rows].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `orders_${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div className="flex flex-col md:flex-row justify-between md:items-end gap-4 mb-4">
        <div>
          <h2 className="font-display text-2xl font-bold">Paid orders</h2>
          <p className="text-sm text-muted-foreground mt-1">
            <span className="text-primary font-mono">{orders.length}</span> order(s) · Total revenue: <span className="text-primary font-mono">₹{totalRevenue.toLocaleString("en-IN")}</span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select value={filterToolId} onChange={(e) => setFilterToolId(e.target.value)} data-testid="orders-tool-filter" className="rounded-sm bg-secondary border border-border px-3 py-2 text-sm font-mono">
            <option value="">All tools</option>
            {tools.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <Button size="sm" variant="outline" onClick={exportCsv} data-testid="orders-export-csv" className="rounded-sm gap-2"><DownloadSimple size={14} weight="bold" /> Export CSV</Button>
        </div>
      </div>
      {loading ? <div className="text-sm text-muted-foreground font-mono">Loading...</div> : (
        <div className="border border-border rounded-sm overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Paid at</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Tools</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead>Coupon</TableHead>
              <TableHead>Payment ID</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {orders.map((o) => (
                <TableRow key={o.order_id} data-testid={`order-${o.order_id}`}>
                  <TableCell className="font-mono text-xs whitespace-nowrap">{o.paid_at ? new Date(o.paid_at).toLocaleString() : "—"}</TableCell>
                  <TableCell className="font-mono text-xs">{o.user_email}</TableCell>
                  <TableCell className="text-sm">{o.user_name}</TableCell>
                  <TableCell className="text-sm">{(o.tools || []).join(", ")}{o.kind === "subscription" && <span className="ml-2 text-[10px] font-mono uppercase tracking-widest text-primary">auto-debit</span>}</TableCell>
                  <TableCell className="text-right font-mono">₹{Number(o.amount_inr).toLocaleString("en-IN")}</TableCell>
                  <TableCell className="font-mono text-xs">{o.coupon_code || "—"}</TableCell>
                  <TableCell className="font-mono text-[10px] text-muted-foreground truncate max-w-[160px]">{o.payment_id || "—"}</TableCell>
                </TableRow>
              ))}
              {orders.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-8">No paid orders yet{filterToolId ? " for this tool" : ""}.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

// ---------- Admin Main ----------
export default function Admin() {
  const [tools, setTools] = useState([]);
  const [users, setUsers] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [accessCodes, setAccessCodes] = useState([]);
  const [groups, setGroups] = useState([]);

  const loadAll = async () => {
    const [t, u, c, g, ac] = await Promise.all([
      api.get("/admin/tools").catch(() => ({ data: [] })),
      api.get("/admin/users").catch(() => ({ data: [] })),
      api.get("/admin/coupons").catch(() => ({ data: [] })),
      api.get("/admin/groups").catch(() => ({ data: [] })),
      api.get("/admin/access-codes").catch(() => ({ data: [] })),
    ]);
    setTools(t.data); setUsers(u.data); setCoupons(c.data); setGroups(g.data); setAccessCodes(ac.data);
  };
  useEffect(() => { loadAll(); }, []);

  const deleteTool = async (id) => { if (window.confirm("Delete this tool?")) { try { await api.delete(`/admin/tools/${id}`); toast.success("Deleted"); loadAll(); } catch (err) { toast.error(extractError(err)); } } };
  const deleteUser = async (id) => { if (window.confirm("Delete this user?")) { try { await api.delete(`/admin/users/${id}`); toast.success("Deleted"); loadAll(); } catch (err) { toast.error(extractError(err)); } } };
  const deleteCoupon = async (id) => { if (window.confirm("Delete this coupon?")) { try { await api.delete(`/admin/coupons/${id}`); toast.success("Deleted"); loadAll(); } catch (err) { toast.error(extractError(err)); } } };
  const deleteAccessCode = async (id) => { if (window.confirm("Delete this access code? Redeemed grants stay with users.")) { try { await api.delete(`/admin/access-codes/${id}`); toast.success("Deleted"); loadAll(); } catch (err) { toast.error(extractError(err)); } } };
  const toggleAccessCodeActive = async (ac) => {
    try { await api.patch(`/admin/access-codes/${ac.id}`, { is_active: !ac.is_active }); loadAll(); }
    catch (err) { toast.error(extractError(err)); }
  };

  const grantTool = async (userId, toolId, durationDays = 30) => {
    try { await api.post(`/admin/users/${userId}/grant`, { tool_id: toolId, duration_days: Number(durationDays) }); toast.success(`Granted ${durationDays}-day access`); loadAll(); }
    catch (err) { toast.error(extractError(err)); }
  };

  return (
    <div data-testid="admin-page" className="min-h-[calc(100vh-4rem)] px-6 md:px-8 py-10">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-primary mb-2">// Admin</div>
          <h1 className="font-display font-black text-4xl tracking-tighter">Control Center</h1>
        </div>

        <Tabs defaultValue="tools" className="w-full">
          <TabsList className="rounded-sm bg-secondary">
            <TabsTrigger value="tools" data-testid="tab-tools" className="rounded-sm">Tools ({tools.length})</TabsTrigger>
            <TabsTrigger value="orders" data-testid="tab-orders" className="rounded-sm">Orders</TabsTrigger>
            <TabsTrigger value="subscriptions" data-testid="tab-subscriptions" className="rounded-sm">Subscriptions</TabsTrigger>
            <TabsTrigger value="offers" data-testid="tab-offers" className="rounded-sm">Offers</TabsTrigger>
            <TabsTrigger value="coupons" data-testid="tab-coupons" className="rounded-sm">Coupons ({coupons.length})</TabsTrigger>
            <TabsTrigger value="access-codes" data-testid="tab-access-codes" className="rounded-sm">Access Codes ({accessCodes.length})</TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users" className="rounded-sm">Users ({users.length})</TabsTrigger>
          </TabsList>

          {/* Tools */}
          <TabsContent value="tools" className="mt-6">
            <div className="flex justify-between items-center mb-4"><h2 className="font-display text-2xl font-bold">HTML Tools · monthly subscription</h2><ToolUploadDialog onCreated={loadAll} /></div>
            <div className="border border-border rounded-sm overflow-x-auto">
              <Table>
                <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Slug</TableHead><TableHead>Category</TableHead><TableHead>Price</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
                <TableBody>
                  {tools.map((t) => (
                    <TableRow key={t.id} data-testid={`admin-tool-row-${t.slug}`}>
                      <TableCell className="font-medium">{t.name}</TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground">{t.slug}</TableCell>
                      <TableCell className="text-sm">{t.category}</TableCell>
                      <TableCell className="font-mono">
                        ₹{t.price_inr}/{t.is_subscription ? periodText(t) : "mo"}
                        {t.is_subscription && <div className="text-[10px] uppercase tracking-widest text-primary" data-testid={`admin-tool-sub-${t.slug}`}>Auto-debit · {t.razorpay_plan_id}</div>}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <ToolEditDialog tool={t} onUpdated={loadAll} />
                          <Button size="sm" variant="outline" className="rounded-sm h-8 text-destructive hover:text-destructive" onClick={() => deleteTool(t.id)} data-testid={`del-tool-${t.slug}`}><Trash size={14} weight="bold" /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {tools.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">No tools yet.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* Orders */}
          <TabsContent value="orders" className="mt-6">
            <OrdersTable tools={tools} />
          </TabsContent>

          <TabsContent value="subscriptions" className="mt-6">
            <SubscriptionsTable />
          </TabsContent>

          <TabsContent value="offers" className="mt-6">
            <OfferReport />
          </TabsContent>

          {/* Coupons */}
          <TabsContent value="coupons" className="mt-6">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h2 className="font-display text-2xl font-bold">Discount Codes</h2>
                <p className="text-sm text-muted-foreground">Percent-off codes. Optionally restrict to specific students or groups.</p>
              </div>
              <CouponDialog onDone={loadAll} tools={tools} users={users} groups={groups} />
            </div>
            <div className="border border-border rounded-sm overflow-x-auto">
              <Table>
                <TableHeader><TableRow><TableHead>Code</TableHead><TableHead>% off</TableHead><TableHead>Scope</TableHead><TableHead>Uses</TableHead><TableHead>Valid until</TableHead><TableHead>Active</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
                <TableBody>
                  {coupons.map((c) => {
                    const scope = (c.allowed_user_ids.length === 0 && c.allowed_groups.length === 0)
                      ? "General"
                      : [c.allowed_user_ids.length ? `${c.allowed_user_ids.length} user${c.allowed_user_ids.length > 1 ? "s" : ""}` : null,
                         c.allowed_groups.length ? `${c.allowed_groups.length} group${c.allowed_groups.length > 1 ? "s" : ""}` : null].filter(Boolean).join(" + ");
                    return (
                      <TableRow key={c.id} data-testid={`admin-coupon-row-${c.code}`}>
                        <TableCell className="font-mono font-bold text-primary">{c.code}</TableCell>
                        <TableCell className="font-mono" data-testid={`coupon-value-${c.code}`}>{c.free_days > 0 ? `${c.free_days} days free · ${c.tool_ids?.length ? `${c.tool_ids.length} tool(s)` : "all subs"}` : `${c.percent_off}%`}</TableCell>
                        <TableCell className="text-xs">{scope}</TableCell>
                        <TableCell className="font-mono text-xs">{c.usage_count}{c.max_uses ? ` / ${c.max_uses}` : ""}</TableCell>
                        <TableCell className="font-mono text-xs">{c.valid_until ? new Date(c.valid_until).toLocaleDateString() : "—"}</TableCell>
                        <TableCell>{c.is_active ? <Check size={16} weight="bold" className="text-primary" /> : <X size={16} weight="bold" className="text-destructive" />}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <CouponDialog initial={c} onDone={loadAll} tools={tools} users={users} groups={groups} />
                            <Button size="sm" variant="outline" className="rounded-sm h-8 text-destructive hover:text-destructive" onClick={() => deleteCoupon(c.id)} data-testid={`del-coupon-${c.code}`}><Trash size={14} weight="bold" /></Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {coupons.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8"><Ticket size={20} weight="duotone" className="inline-block mr-2" /> No coupons yet. Create your first one.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* Users */}
          <TabsContent value="access-codes" className="mt-6">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h2 className="font-display text-2xl font-bold">Free-Access Codes</h2>
                <p className="text-sm text-muted-foreground">Grant free tool access for N days to students on an email allowlist.</p>
              </div>
              <AccessCodeCreateDialog tools={tools} onCreated={loadAll} />
            </div>
            <div className="border border-border rounded-sm overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Code</TableHead>
                    <TableHead>Tools</TableHead>
                    <TableHead>Duration</TableHead>
                    <TableHead>Allowlist</TableHead>
                    <TableHead>Active</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {accessCodes.map((ac) => (
                    <TableRow key={ac.id} data-testid={`admin-ac-row-${ac.code}`}>
                      <TableCell>
                        <div className="font-mono font-bold text-primary">{ac.code}</div>
                        {ac.description && <div className="text-xs text-muted-foreground mt-0.5">{ac.description}</div>}
                      </TableCell>
                      <TableCell className="font-mono text-xs">{ac.tool_ids.length} tool(s)</TableCell>
                      <TableCell className="font-mono text-xs">{ac.duration_days}d</TableCell>
                      <TableCell className="font-mono text-xs">
                        <div>{ac.allowlist_used}/{ac.allowlist_total} redeemed</div>
                        <div className="text-muted-foreground">{ac.allowlist_remaining} pending</div>
                      </TableCell>
                      <TableCell>
                        <button onClick={() => toggleAccessCodeActive(ac)} data-testid={`ac-toggle-${ac.code}`} className="inline-flex">
                          {ac.is_active ? <Check size={16} weight="bold" className="text-primary" /> : <X size={16} weight="bold" className="text-destructive" />}
                        </button>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <AccessCodeManageDialog code={ac} tools={tools} onUpdated={loadAll} />
                          <Button size="sm" variant="outline" className="rounded-sm h-8 text-destructive hover:text-destructive" onClick={() => deleteAccessCode(ac.id)} data-testid={`del-ac-${ac.code}`}><Trash size={14} weight="bold" /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {accessCodes.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8"><Gift size={20} weight="duotone" className="inline-block mr-2" /> No access codes yet. Create one for a student batch.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="users" className="mt-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-display text-2xl font-bold">Users</h2>
              <BulkGrantDialog tools={tools} onGranted={loadAll} />
            </div>
            <p className="text-sm text-muted-foreground mb-4">Click on a user's group cell to tag them — used for coupon targeting. Use "Grant tool" for individual grants or "Bulk grant" for many users at once.</p>
            <div className="border border-border rounded-sm overflow-x-auto">
              <Table>
                <TableHeader><TableRow><TableHead>Email</TableHead><TableHead>Name</TableHead><TableHead>Role</TableHead><TableHead>Groups</TableHead><TableHead>Active tools</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
                <TableBody>
                  {users.map((u) => (
                    <TableRow key={u.id} data-testid={`admin-user-${u.email}`}>
                      <TableCell className="font-mono text-xs">{u.email}</TableCell>
                      <TableCell>{u.name}</TableCell>
                      <TableCell className="font-mono text-xs uppercase">{u.role}</TableCell>
                      <TableCell><UserGroupsDialog user={u} onUpdated={loadAll} /></TableCell>
                      <TableCell className="text-xs font-mono">{u.active_tool_ids?.length || 0}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2 items-center">
                          <UserGrantPopover userId={u.id} userEmail={u.email} tools={tools} onGranted={grantTool} />
                          <Button size="sm" variant="outline" className="rounded-sm h-8 text-destructive hover:text-destructive" onClick={() => deleteUser(u.id)} disabled={u.role === "admin"} data-testid={`del-user-${u.email}`}><Trash size={14} weight="bold" /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
